// React + TypeScript component for the provided design
// Replace imageUrl with the uploaded file path: /mnt/data/02-UserDashboard.png

import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./Dashboard.scss";
import FeaturedAgents, { Agent } from "../components/FeaturedAgents";
import MyAgents from "../components/MyAgents";
import KeepWorking from "../components/KeepWorking";
import WorkflowsByFunction from "../components/WorkflowsByFunction";
import Sidebar from "../components/Sidebar";
import { useMsal } from "@azure/msal-react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../store";
import { clearUser } from "../store/authSlice";
import {
  fetchAgents,
  sendTermsAcceptance,
  updateAgentAcceptance,
} from "../store/agentSlice";
import { v4 as uuid } from "uuid";
import PageLoader from "../components/PageLoader";
import ChatPanelG42 from "../components/ChatPanelG42";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AccessDeniedModal from "../components/AccessDeniedModal";
import TermsAndConditionsModal from "../components/TermsAndConditionsModal";
import { Agents } from "../types/master";

const imageUrl = "/mnt/data/02-UserDashboard.png";

// Agent IDs that should open using their endpoint URL in a new tab
const EXTERNAL_ENDPOINT_AGENTS = [19, 31, 32, 33, 34, 27, 36];

const featuredAgents: Agent[] = [
  { id: "a1", title: "Agent Name", subtitle: "Lorem ipsum dolor sit amet" },
  { id: "a2", title: "Agent Name", subtitle: "Lorem ipsum dolor sit amet" },
  { id: "a3", title: "Agent Name", subtitle: "Lorem ipsum dolor sit amet" },
  { id: "a4", title: "Agent Name", subtitle: "Lorem ipsum dolor sit amet" },
  { id: "a5", title: "Agent Name", subtitle: "Lorem ipsum dolor sit amet" },
  { id: "a6", title: "Agent Name", subtitle: "Lorem ipsum dolor sit amet" },
  { id: "a7", title: "Agent Name", subtitle: "Lorem ipsum dolor sit amet" },
  { id: "a8", title: "Agent Name", subtitle: "Lorem ipsum dolor sit amet" },
  { id: "a9", title: "Agent Name", subtitle: "Lorem ipsum dolor sit amet" },
  { id: "a10", title: "Agent Name", subtitle: "Lorem ipsum dolor sit amet" },
  { id: "a11", title: "Agent Name", subtitle: "Lorem ipsum dolor sit amet" },
  { id: "a12", title: "Agent Name", subtitle: "Lorem ipsum dolor sit amet" },
];

const myAgents: Agent[] = [
  {
    id: "m1",
    title: "New board member",
    subtitle: "Announcing an addition to the board",
  },
  {
    id: "m2",
    title: "New partnership",
    subtitle: "Template for new corporate partnership",
  },
  {
    id: "m3",
    title: "Internal releases",
    subtitle: "Formal announcements focused within the company",
  },
  {
    id: "m4",
    title: "Financial reports",
    subtitle: "Automation of reports to be sent to the board",
  },
];

const keepWorkingItems = [
  {
    id: "w1",
    title: "Press Release and Corporate Communications Agent",
    description:
      "Create an announcement for the hiring of Amy Hood as G42’s new CFO. Include her background as Microsoft CFO since 2013, her achievements (first woman CFO at Microsoft, operational focus, major deals like GitHub acquisition, shift to cloud, culture-building), and highlight what she brings to G42. Add a quote from Amy expressing excitement to join G42 and a quote from Peng Xiao, G42’s Group CEO.",
    timestamp: "Sat 12:35PM",
  },
  {
    id: "w2",
    title: "Candidate Screening and Reconnect Agent",
    description:
      "Generate 5 tweet prompts about G42’s AI supercomputing projects in an inspiring tone with hashtags #AIWithPurpose and #Innovation.",
    timestamp: "Fri 11:23AM",
  },
  {
    id: "w3",
    title: "Press Release Agent",
    description:
      "Create an announcement for the hiring of Amy Hood as G42’s new CFO. Include her background as Microsoft CFO since 2013, her achievements (first woman CFO at Microsoft, operational focus, major deals like GitHub acquisition, shift to cloud, culture-building), and highlight what she brings to G42. Add a quote from Amy expressing excitement to join G42 and a quote from Peng Xiao, G42’s Group CEO.",
    timestamp: "Mon 23:05PM",
  },
];

export default function Dashboard(): JSX.Element {
  const user = useSelector((state: RootState) => state.auth.user);
  const {
    loading: agentsLoading,
    data: allAgents,
    termsAcceptanceLoading,
  } = useSelector((state: RootState) => state.agents);

  const sessionId = useMemo(() => uuid(), []);
  const { instance } = useMsal();
  const dispatch = useAppDispatch();
  const [showAccessDeniedModal, setShowAccessDeniedModal] = useState(false);

  // Check if terms and conditions need to be accepted
  const termsAgent = useMemo(() => {
    return allAgents.find((agent) => agent.id === 501);
  }, [allAgents]);

  const showTermsModal = useMemo(() => {
    return termsAgent && termsAgent.is_access === 0;
  }, [termsAgent]);

  const onClickLogout = async () => {
    try {
      await instance.logoutPopup({
        postLogoutRedirectUri: "/",
      });
      dispatch(clearUser());
      window.location.href = "/";
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  useEffect(() => {
    dispatch(fetchAgents());
  }, [dispatch]);

  // Helper function to determine the correct URL identifier for an agent
  const getAgentNavigationIdentifier = useCallback(
    (agent: Agents): string | number => {
      // If framework_name is missing or empty, use numeric ID
      if (
        !agent.agent_framework_name ||
        agent.agent_framework_name.trim() === ""
      ) {
        return agent.id;
      }

      // Check if this framework_name is used by multiple agents (duplicates)
      const agentsWithSameName = allAgents.filter(
        (a) => a.agent_framework_name === agent.agent_framework_name,
      );

      // If multiple agents share the same framework_name, use numeric ID to avoid conflicts
      if (agentsWithSameName.length > 1) {
        return agent.id;
      }

      // Otherwise use framework_name for cleaner URLs
      return agent.agent_framework_name;
    },
    [allAgents],
  );

  // Centralized handler for agent access and navigation
  const handleAgentClick = useCallback(
    (agentId: number) => {
      // Find the agent in the list
      const agent = allAgents.find((a) => a.id === agentId);

      // If agent doesn't exist, don't do anything
      if (!agent) {
        return;
      }

      // Check if user has access to this agent
      if (agent.is_access === 0) {
        setShowAccessDeniedModal(true);
        return;
      }

      // Check if external endpoint agent has endpoint URL
      if (EXTERNAL_ENDPOINT_AGENTS.includes(agentId) && !agent.endpoint) {
        console.warn(
          `Agent ${agentId} is configured for external endpoint but has no endpoint URL`,
        );
        return;
      }

      // Handle navigation based on agent type
      if (EXTERNAL_ENDPOINT_AGENTS.includes(agentId)) {
        window.open(agent.endpoint, "_blank");
      } else {
        // Use smart detection to determine whether to use framework_name or numeric ID
        const urlIdentifier = getAgentNavigationIdentifier(agent);
        window.open(`/chat/${urlIdentifier}?sessionId=${sessionId}`, "_blank");
      }
    },
    [allAgents, sessionId, getAgentNavigationIdentifier],
  );

  const closeAccessDeniedModal = useCallback(() => {
    setShowAccessDeniedModal(false);
  }, []);

  const handleTermsAccept = useCallback(async () => {
    // Extract user_id from any agent (all have the same user_id)
    const userId = allAgents[0]?.user_id;

    if (!userId) {
      console.error("User ID not found in agents data");
      return;
    }

    try {
      await dispatch(sendTermsAcceptance(userId)).unwrap();
      // Update the agent's is_access status locally
      if (termsAgent) {
        dispatch(updateAgentAcceptance(termsAgent.id));
      }
    } catch (error) {
      console.error("Failed to send terms acceptance:", error);
    }
  }, [dispatch, allAgents, termsAgent]);

  const [isFixed, setIsFixed] = useState(false);

  useEffect(() => {
    if (agentsLoading) return;

    const mainArea = document.querySelector(".main-area") as HTMLElement | null;
    if (!mainArea) return;

    const onScroll = () => {
      setIsFixed(mainArea.scrollTop >= 400);
    };

    mainArea.addEventListener("scroll", onScroll);

    return () => mainArea.removeEventListener("scroll", onScroll);
  }, [agentsLoading]);

  // Show loader while agents are being fetched
  if (agentsLoading) {
    return <PageLoader label="Loading agents..." activeNav="home" />;
  }

  return (
    <div className="dashboard-root">
      <TermsAndConditionsModal
        open={showTermsModal || false}
        onAccept={handleTermsAccept}
        loading={termsAcceptanceLoading}
      />
      <Sidebar
        activeNav="home"
        user={user || undefined}
        onLogout={onClickLogout}
      />
      <main className="main-area">
        <header className="header">
          <div className="header-left">
            <h1 className="page-title">DASHBOARD</h1>
          </div>
          <div className="header-right">
            <div className="beta-info">
              <button
                className="beta-info-icon"
                aria-label="Beta rollout information"
              >
                <InfoOutlinedIcon fontSize="small" />
              </button>
              <div className="beta-info-popover">
                Current version rolled out to select SMEs, full launch coming
                soon. Check the “Watch It Work” video for an exclusive preview.
              </div>
            </div>
          </div>
        </header>

        <FeaturedAgents onAgentClick={handleAgentClick} />
        <div className="scrollContent">
          <section className="content-grid">
            <div className="left-column">
              {/* <KeepWorking items={keepWorkingItems} /> */}

              <MyAgents agents={myAgents} onAgentClick={handleAgentClick} />
              <WorkflowsByFunction onAgentClick={handleAgentClick} />
            </div>

            {/* <div className="right-column">
              <ChatPanelG42
                sessionId={sessionId}
                agentName="PR Agent"
                subtitle="Get started now"
                isFixed={isFixed}
                enableGenerateImageToggle={false}
              />
            </div> */}
          </section>

          <footer className="footer-note">
            <img alt="bg" src={imageUrl} className="bg-preview" />
          </footer>
        </div>
      </main>

      <AccessDeniedModal
        open={showAccessDeniedModal}
        onClose={closeAccessDeniedModal}
      />
    </div>
  );
}
